import { collection, doc, getDoc, increment, setDoc, arrayUnion } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { Log } from "@/models/log";
import {
  SelectedDifficulty,
  SelectedDifficultyLocalStorage,
  PhoneNumber,
} from "@/models/shared";
import { Stage } from "@/models/stage";
import { DBUser, User, UserWithPhoneNumber } from "@/models/user";
import { currentTimeInSeconds } from "@/utils/common";
import { Collections } from "@/constants/firebase.collections";
import { AppConfig } from "@/config/app.config";

import { db, functions } from ".";
import { setLogDoc } from "./logs";

// Users Collection Ref
const usersRef = collection(db, Collections.USERS);

export const updateUserDoc = async (id: PhoneNumber, data: Partial<User>) => {
  const userDoc = doc(usersRef, id);
  await setDoc(userDoc, data, { merge: true });
};

export const getUserDoc = async (id: PhoneNumber) => {
  const userDocRef = doc(usersRef, id);
  const snapshot = await getDoc(userDocRef);
  if (snapshot.exists()) {
    const userData = {
      phone_number: snapshot.id,
      ...snapshot.data(),
    } as UserWithPhoneNumber;
    
    // Sync the user's batch to localStorage (Firestore is the source of truth)
    if (userData.selected_batch) {
      localStorage.setItem("selectedBatch", userData.selected_batch);
    }
    
    return userData;
  } else {
    throw new Error("User not found");
  }
};

export const getOrSetUserWithPhoneNumber = async (
  phone_number: PhoneNumber,
  selected_difficulty?: SelectedDifficultyLocalStorage
): Promise<DBUser | null> => {
  
  const userDocSnapshot = await getDoc(doc(usersRef, phone_number));

  if (userDocSnapshot.exists()) {
    return {
      ...userDocSnapshot.data(),
      phone_number: userDocSnapshot.id,
    } as DBUser;
  } else if (selected_difficulty != null) {
    
    // if the user doesn't exist, create a new user in firestore

    // Get the selected batch and its config
    const selectedBatch = localStorage.getItem("selectedBatch") || AppConfig.DEFAULT_BATCH;
    const batchConfig = AppConfig.BATCHES[selectedBatch];
    const policeServerEpisode = batchConfig?.policeServerEpisode || AppConfig.DEFAULTS.policeServerEpisode;
    const policeServerUsername = batchConfig?.policeServerUsername || AppConfig.DEFAULTS.policeServerUsername;
    

    const createNewUser = httpsCallable<
      {
        phone_number: PhoneNumber;
        selected_difficulty: SelectedDifficulty;
        episode?: string;
        username?: string;
      },
      DBUser
    >(functions, "createNewUser");



    try {
      const { data: createdUser } = await createNewUser({
        phone_number,
        selected_difficulty,
        episode: policeServerEpisode,
        username: policeServerUsername,
      });
      
      
      // Save the batch to the user document (frontend saves this, not cloud function)
      await updateUserDoc(phone_number, {
        selected_batch: selectedBatch,
      });

      // Create a log for started_game
      const startedGameLog: Log = {
        type: "started_game",
        timestamp: currentTimeInSeconds(),
      };

      setLogDoc(phone_number, startedGameLog);

      // Call the cloud function for triggering the twilio flow
      // changed from episode 002
      fetch(
        `https://startgametwilio-276526588706.europe-west3.run.app?phone_number=${phone_number}`,
        { mode: "no-cors" }
      );

      return createdUser;
    } catch (error) {
      throw error;
    }
  }

  return null;
};

export const incrementHintsUsedInUserDoc = async (
  id: PhoneNumber,
  data: { stage_number: Stage["stage_number"] }
) => {
  const userDoc = doc(usersRef, id);

  await setDoc(
    userDoc,
    {
      hints_used: {
        [`stage_${data.stage_number}`]: increment(1),
      },
    },
    { merge: true }
  );
};

export const incrementMaterialDownloadsCount = async (id: PhoneNumber) => {
  const userDoc = doc(usersRef, id);

  await setDoc(
    userDoc,
    {
      material_downloads_count: increment(1),
    },
    { merge: true }
  );
};

export const addDownloadedMaterial = async (id: PhoneNumber, materialId: string) => {
  const userDoc = doc(usersRef, id);

  await setDoc(
    userDoc,
    {
      downloaded_materials: arrayUnion(materialId),
    },
    { merge: true }
  );
};
